import dayjs from "dayjs";
import { BookingType, IBookingQuestionInput, IVehicle } from "../../../reducers/bookings/types";
import { IBookingQuestion, IServiceLocation, TBQAnswer } from "../../../reducers/website/types";
import _ from "lodash"
import i18next from 'i18next';
import { IUser } from "../../../reducers/auth/types";
import { useTranslation } from "react-i18next";
import { getDistanceFromLatLonInKm, ICoordinates, isValidPostalCodeFormat, reverseGeocode } from "../../../utils/google";

export function capitalizeFirstLetter(name: string) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export enum FleetTypes {
  OWN = "OWN",
  SHARED = "SHARED",
}

export const rateTypes = [
  {
    rateTypeName: "hourly",
    rateTypeDuration: 60,
  },
  {
    rateTypeName: "daily",
    rateTypeDuration: 1440,
  },
  {
    rateTypeName: "weekly",
    rateTypeDuration: 10080,
  },
  {
    rateTypeName: "monthly",
    rateTypeDuration: 43200,
  },
];

export const billingCycles = [
  {
    billingCycleName: "none",
  },
  {
    billingCycleName: "weekly",
    billingCycleDuration: 10080,
  },
  {
    billingCycleName: "monthly",
    billingCycleDuration: 43200,
  },
];

export const customerTypes: any = [
  {
    value: "individual",
    label: "Individual",
  },
  {
    value: "business",
    label: "Business",
  },
];
export function getRentalPeriod(
  startDate: string,
  endDate: string,
  rateTypeDuration: number,
  rateTypeName: string
): string {
  let rentalPeriod = "";
  const timeDiff: number = dayjs(endDate).diff(startDate);
  const rentalTime: number = Math.ceil(timeDiff / (rateTypeDuration * 60000));
  rentalPeriod = rentalTime + " ";
  switch (rateTypeName) {
    case "hourly":
      rentalPeriod += "hour(s)";
      break;
    case "daily":
      rentalPeriod += "day(s)";
      break;
    case "weekly":
      rentalPeriod += "week(s)";
      break;
    case "monthly":
      rentalPeriod += "month(s)";
      break;
  }
  return rentalPeriod;
}

export enum Constants {
  MINUTE_TO_MILLISECOND = 60000,
  BOOKING_ADVANCE_START_TIME = 5, // in minutes
}

export enum BookingFlowStage {
  BASE_DETAILS,
  ADDONS_SELECTION,
  PERSONAL_DETAILS,
  QUOTE_VIEW,
  PAYMENT_VIEW,
}

export enum SubscriptionFlowStage {
  CATALOGUE_DETAILS,
  VEHICLE_DETAILS,
  PERSONAL_DETAILS,
  VERIFICATION,
}

export const addonTypes = ["INSURANCE", "CUSTOM", "VALET"];

export enum BookingStatus {
  IN_PROGRESS = "IN_PROGRESS",
}

// copied from view/fleet/vehiclePriceGroups/utils from original

export const initialBasePrices = [
  {
    rateTypeName: "hourly",
    rate: 0,
    rateTypeDuration: 60,
    longTerm: false,
    mileageLimit: 0,
    pricePerExtraMile: 0,
  },
  {
    rateTypeName: "daily",
    rate: 0,
    rateTypeDuration: 1440,
    longTerm: false,
    mileageLimit: 0,
    pricePerExtraMile: 0,
  },
  {
    rateTypeName: "weekly",
    rate: 0,
    rateTypeDuration: 10080,
    longTerm: false,
    mileageLimit: 0,
    pricePerExtraMile: 0,
  },
  {
    rateTypeName: "monthly",
    rate: 0,
    rateTypeDuration: 43200,
    longTerm: false,
    mileageLimit: 0,
    pricePerExtraMile: 0,
  },
];
 
export const bookingCategories = {
  [BookingType.RENTAL]: {
    label: "Rentals",
    subLabel: "Book vehicle for hours and days",
    buttonText: "Book Now",
    maxVehiclesAllowed: 0,
    showMileage: true,
    showVehicleAvailableCount: true,
    showDepositDetails: true,
    allowUpdate: true,
    showFilters: true,
    showPriceSort: true,
    showKeylessVehiclesToggle: true,
    enabled: false,
  },
  [BookingType.SUBSCRIPTION]: {
    label: "Subscriptions",
    subLabel: "Subscribe vehicle for weekly/monthly",
    buttonText: "Subscribe Now",
    maxVehiclesAllowed: 1,
    showMileage: true,
    showVehicleAvailableCount: false,
    showDepositDetails: true,
    allowUpdate: true,
    showFilters: true,
    showPriceSort: false,
    showKeylessVehiclesToggle: false,
    enabled: false,
  },
  [BookingType.TEST_DRIVE]: {
    label: "Test Drive",
    subLabel: "Test Drive our Premium Vehicles",
    buttonText: "Book Now",
    maxVehiclesAllowed: 1,
    showMileage: false,
    showVehicleAvailableCount: false,
    showDepositDetails: false,
    allowUpdate: true,
    showFilters: false,
    showPriceSort: false,
    showKeylessVehiclesToggle: false,
    enabled: false,
  },
};

export const sortSubscriptionVehicles = (
  vehicles: IVehicle[],
  bookedVehicleId: string | undefined
): IVehicle[] => {
  if (!bookedVehicleId) return vehicles;
  return vehicles.sort((a, b) => {
    if (a.id === bookedVehicleId) return -1;
    if (b.id === bookedVehicleId) return 1;
    return 0;
  });
};

export const getMergedPreBookingQuestions = (
  bookingQuestions: IBookingQuestionInput[],
  branchQuestions: IBookingQuestion[]
): IBookingQuestion[] => {

  if (!bookingQuestions.length) {
    return branchQuestions.filter((q: IBookingQuestion) => q.B2B2C);
  }

  let mergedQuestions: IBookingQuestion[] = _.cloneDeep(branchQuestions);

  bookingQuestions.forEach((bq) => {
    const idx: number = mergedQuestions.findIndex((q: IBookingQuestion) => (q.question === bq.question && q.answerType == bq.answerType));
    const answer: TBQAnswer = bq?.answer || "";
    if (idx > -1) {
      mergedQuestions[idx] = { ...mergedQuestions[idx], answer: answer };
      if (mergedQuestions[idx].B2B && !mergedQuestions[idx].B2B2C) {
        /* 
            If the question exists in the booking and is only enabled for the webapp,
            it will be displayed but rendered as disabled. 
        */
        mergedQuestions[idx].B2B2C = true;
        mergedQuestions[idx].required = false;
        mergedQuestions[idx].deleted = true;
      }
    }
    else {
      mergedQuestions.push({
        question: bq.question,
        answerType: bq.answerType,
        deleted: true,
        required: false,
        answer: answer,
        B2B: true,
        B2B2C: true,
        options: typeof answer === "string" ? [answer] : [],
      });
    }
  })

  return mergedQuestions.filter((q: IBookingQuestion) => q.B2B2C);
}


export const getNameForAuthorizedSignatory = (id?: string, user?: IUser) => {
  if (id && user?.businesscustomers?.authorizedSignatories) {
    const authorisedSignatory = user.businesscustomers.authorizedSignatories.find(
      (ele) => ele.id === id
    );

    if (authorisedSignatory) {
      return `${authorisedSignatory.firstName} ${authorisedSignatory.lastName}`;
    }
  }
};

interface ISortLocationsParams {
  locationsArray: IServiceLocation[];
  searchText?: string;
  coordinates?: ICoordinates;
};

export const searchLocations = async ({
  locationsArray,
  searchText,
  coordinates,
}: ISortLocationsParams): Promise<{
  locations: IServiceLocation[];
  isDistanceSorted: boolean;
}> => {
  try {
    /* --------------------------------------------------
     * 1. COORDINATES (highest priority)
     * -------------------------------------------------- */
    if (coordinates?.lat && coordinates?.lon) {
      return {
        locations: [...locationsArray].sort((a, b) => {
          if (!a.address?.geometry || !b.address?.geometry) return 0;

          const distanceA = getDistanceFromLatLonInKm({
            coordinate1: coordinates,
            coordinate2: {
              lat: a.address.geometry.latitude,
              lon: a.address.geometry.longitude,
            },
          });

          const distanceB = getDistanceFromLatLonInKm({
            coordinate1: coordinates,
            coordinate2: {
              lat: b.address.geometry.latitude,
              lon: b.address.geometry.longitude,
            },
          });

          return distanceA - distanceB;
        }),
        isDistanceSorted: true,
      };
    }

    /* --------------------------------------------------
     * Normalize search text
     * -------------------------------------------------- */
    const normalizedSearch = searchText?.trim();

    if (!normalizedSearch) {
      return {
        locations: locationsArray,
        isDistanceSorted: false,
      };
    }

    /* --------------------------------------------------
     * 2. ZIPCODE FLOW (only if valid zipcode)
     * -------------------------------------------------- */
    if (isValidPostalCodeFormat(normalizedSearch)) {
      // Exact / partial zipcode match
      const zipMatches = locationsArray.filter((location) =>
        location.address?.zipcode?.toString().includes(normalizedSearch)
      );

      if (zipMatches.length) {
        return {
          locations: zipMatches,
          isDistanceSorted: false,
        };
      }

      // Reverse geocode
      const referenceCoords = await reverseGeocode(normalizedSearch);

      if (referenceCoords) {
        return {
          locations: [...locationsArray].sort((a, b) => {
            if (!a.address?.geometry || !b.address?.geometry) return 0;

            const distanceA = getDistanceFromLatLonInKm({
              coordinate1: referenceCoords,
              coordinate2: {
                lat: a.address.geometry.latitude,
                lon: a.address.geometry.longitude,
              },
            });

            const distanceB = getDistanceFromLatLonInKm({
              coordinate1: referenceCoords,
              coordinate2: {
                lat: b.address.geometry.latitude,
                lon: b.address.geometry.longitude,
              },
            });

            return distanceA - distanceB;
          }),
          isDistanceSorted: true,
        };
      }
    }

    /* --------------------------------------------------
     * 3. NAME SEARCH (fallback)
     * -------------------------------------------------- */
    const loweredSearch = normalizedSearch.toLowerCase();

    const nameMatches = locationsArray.filter((location) =>
      location.name?.toLowerCase().includes(loweredSearch)
    );

    if (nameMatches.length) {
      return {
        locations: nameMatches,
        isDistanceSorted: false,
      };
    }

    /* --------------------------------------------------
     * 4. NOTHING MATCHED
     * -------------------------------------------------- */
    return {
      locations: [],
      isDistanceSorted: false,
    };
  } catch (error) {
    console.error("Failed to search locations.", {
      searchText,
      errorMessage: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
    });

    return {
      locations: locationsArray,
      isDistanceSorted: false,
    };
  }
};

export const NameTitleOptions: string[] = ["Mr.", "Mrs.", "Ms.", "Miss", "Dr."];
